import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Bot, User, Send, Loader2, Check, Plus, Trash2, ChevronDown, MessageSquare, Sparkles, ArrowRight, RefreshCw, ArrowUpDown, Zap, Clock, AlertTriangle, History, BarChart3, Activity, Target, AlertCircle, Settings2 } from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API_BASE = getApiUrl();

// Robust fetch that handles all edge cases
const robustFetch = async (path, options = {}) => {
  const token = localStorage.getItem('auth_token');
  const tenantId = localStorage.getItem('active_tenant_id');

  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
  };

  const url = `${API_BASE}${path}`;
  const res = await fetch(url, { ...options, headers });

  // Read text first — single read, no "body already read" possible
  const text = await res.text();

  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { msg = JSON.parse(text).detail || msg; } catch {}
    throw new Error(msg);
  }

  try {
    return JSON.parse(text);
  } catch {
    throw new Error(`Réponse invalide du serveur`);
  }
};

const genId = () => `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
const cleanText = (text) => text.replace(/```actions[\s\S]*?```/g, '').trim();

// ════════════════════════════════════════
// ACTION CARD
// ════════════════════════════════════════
const ActionCard = ({ action, onExecute, executing, executed }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const configs = {
    add_stages: { label: 'Ajouter étapes', icon: Plus, bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', btn: 'bg-emerald-600 hover:bg-emerald-500' },
    add_transitions: { label: 'Ajouter transitions', icon: ArrowRight, bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', btn: 'bg-blue-600 hover:bg-blue-500' },
    update_stage: { label: 'Modifier étape', icon: RefreshCw, bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', btn: 'bg-amber-600 hover:bg-amber-500' },
    delete_stage: { label: 'Supprimer étape', icon: Trash2, bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', btn: 'bg-red-600 hover:bg-red-500' },
    reorder_stages: { label: 'Réordonner étapes', icon: ArrowUpDown, bg: 'bg-purple-500/10', border: 'border-purple-500/30', text: 'text-purple-400', btn: 'bg-purple-600 hover:bg-purple-500' },
    set_triggers: { label: 'Configurer triggers', icon: Zap, bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', btn: 'bg-cyan-600 hover:bg-cyan-500' },
  };
  const c = configs[action.type] || configs.add_stages;
  const Icon = c.icon;

  const getDetail = () => {
    switch (action.type) {
      case 'add_stages': return action.stages?.map(s => s.nom).join(', ') || '';
      case 'add_transitions': return action.transitions?.map(t => `${t.from_stage} → ${t.to_stage}`).join(' | ') || `${action.transitions?.length || 0} transition(s)`;
      case 'update_stage': return `${action.stage_id}: ${Object.keys(action.updates || {}).join(', ')}`;
      case 'delete_stage': return action.stage_id;
      case 'reorder_stages': return `${action.order?.length || 0} étape(s)`;
      case 'set_triggers': {
        const parts = [];
        if (action.triggers?.entry_actions?.length) parts.push(`${action.triggers.entry_actions.length} action(s) entrée`);
        if (action.triggers?.exit_triggers?.length) parts.push(`${action.triggers.exit_triggers.length} trigger(s) sortie`);
        return `${action.stage_id}: ${parts.join(', ')}`;
      }
      default: return '';
    }
  };

  const isDestructive = action.type === 'delete_stage';
  const handleClick = () => {
    if (isDestructive && !confirmDelete) {
      setConfirmDelete(true);
      setTimeout(() => setConfirmDelete(false), 4000);
      return;
    }
    setConfirmDelete(false);
    onExecute(action);
  };

  return (
    <div data-testid={`action-card-${action.type}`} className={`${c.bg} ${c.border} border rounded-lg p-3`}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-4 h-4 ${c.text}`} />
        <span className={`text-xs font-bold ${c.text} uppercase tracking-wide`}>{c.label}</span>
      </div>
      <p className="text-xs text-slate-300 mb-3">{getDetail()}</p>
      {executed ? (
        <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="action-applied"><Check className="w-3 h-3" /> Appliqué</span>
      ) : (
        <button onClick={handleClick} disabled={executing} data-testid={`execute-action-${action.type}`}
          className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all text-white ${executing ? 'bg-slate-700 cursor-wait' : confirmDelete ? 'bg-red-700 hover:bg-red-600 animate-pulse' : c.btn}`}>
          {executing ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : confirmDelete ? <AlertTriangle className="w-3 h-3 inline mr-1" /> : <Check className="w-3 h-3 inline mr-1" />}
          {executing ? 'Application...' : confirmDelete ? 'Confirmer la suppression' : 'Appliquer'}
        </button>
      )}
    </div>
  );
};

// ════════════════════════════════════════
// MINI PIPELINE PREVIEW
// ════════════════════════════════════════
const PipelinePreview = ({ refreshKey }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await robustFetch(`/api/pipeline-workflow/stages`);
        setStages(data.stages || data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [refreshKey]);

  const groups = useMemo(() => {
    const g = {};
    stages.forEach(s => {
      const cat = s.categorie || 'autre';
      if (!g[cat]) g[cat] = [];
      g[cat].push(s);
    });
    return g;
  }, [stages]);

  const catLabels = { avant_r1: 'Avant R1', apres_r1: 'Après R1', final: 'Final', autre: 'Autre' };
  const catColors = { avant_r1: 'text-blue-400', apres_r1: 'text-violet-400', final: 'text-emerald-400', autre: 'text-slate-400' };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;

  return (
    <div className="space-y-4" data-testid="pipeline-preview">
      <span className="text-xs font-bold text-white">{stages.length} étapes</span>
      {Object.entries(groups).map(([cat, stgs]) => (
        <div key={cat}>
          <div className={`text-[11px] font-semibold uppercase tracking-wider ${catColors[cat] || 'text-slate-400'} mb-2`}>
            {catLabels[cat] || cat} ({stgs.length})
          </div>
          <div className="space-y-1">
            {stgs.map(s => (
              <div key={s.stage_id || s.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#6B7280' }} />
                <span className="text-xs text-slate-300 truncate">{s.nom || s.stage_id}</span>
                {s.next?.length > 0 && <span className="text-[10px] text-slate-600 ml-auto">{s.next.length} sortie(s)</span>}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

// ════════════════════════════════════════
// CHANGES HISTORY PANEL
// ════════════════════════════════════════
const ChangesHistory = ({ refreshKey }) => {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await robustFetch(`/api/workflow-ai/changes-log?limit=15`);
        setChanges(data.changes || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [refreshKey]);

  const actionIcons = {
    add_stages: { icon: Plus, color: 'text-emerald-400' },
    add_transitions: { icon: ArrowRight, color: 'text-blue-400' },
    update_stage: { icon: RefreshCw, color: 'text-amber-400' },
    delete_stage: { icon: Trash2, color: 'text-red-400' },
    reorder_stages: { icon: ArrowUpDown, color: 'text-purple-400' },
    set_triggers: { icon: Zap, color: 'text-cyan-400' },
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;
  if (changes.length === 0) return (
    <div className="flex flex-col items-center justify-center h-32 text-center">
      <History className="w-8 h-8 text-slate-700 mb-2" />
      <p className="text-xs text-slate-600">Aucune modification pour le moment</p>
    </div>
  );

  return (
    <div className="space-y-2" data-testid="changes-history">
      {changes.map((c, i) => {
        const ai = actionIcons[c.action_type] || { icon: Clock, color: 'text-slate-400' };
        const Icon = ai.icon;
        return (
          <div key={i} className="flex gap-3 px-2 py-2 rounded-lg bg-slate-800/30 border border-slate-700/20">
            <Icon className={`w-3.5 h-3.5 ${ai.color} flex-shrink-0 mt-0.5`} />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-slate-300 truncate">{c.summary}</p>
              <p className="text-[10px] text-slate-600 mt-0.5">{c.timestamp}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ════════════════════════════════════════
// PIPELINE ANALYSIS PANEL
// ════════════════════════════════════════
const HealthBadge = ({ score }) => {
  const color = score >= 80 ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
    : score >= 50 ? 'text-amber-400 bg-amber-500/10 border-amber-500/30'
    : 'text-red-400 bg-red-500/10 border-red-500/30';
  const label = score >= 80 ? 'Bon' : score >= 50 ? 'Attention' : 'Critique';
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-bold ${color}`} data-testid="health-score">
      <Activity className="w-3 h-3" />
      {score}/100 — {label}
    </div>
  );
};

const BarChart = ({ items, maxValue }) => (
  <div className="space-y-2">
    {items.map((item, i) => (
      <div key={i}>
        <div className="flex justify-between text-[11px] mb-0.5">
          <span className="text-slate-400 truncate max-w-[60%]">{item.name}</span>
          <span className="text-slate-500">{item.count} <span className="text-slate-600">({item.percent}%)</span></span>
        </div>
        <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-violet-600 to-blue-500 transition-all duration-500"
            style={{ width: `${Math.max(2, (item.count / maxValue) * 100)}%` }} />
        </div>
      </div>
    ))}
  </div>
);

const PipelineAnalysis = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        setData(await robustFetch(`/api/workflow-ai/analyze`));
      } catch {}
      setLoading(false);
    };
    load();
  }, [refreshKey]);

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;
  if (!data) return <p className="text-xs text-slate-600 text-center">Erreur de chargement</p>;

  const maxLeads = Math.max(...(data.top_stages?.map(s => s.count) || [1]));

  return (
    <div className="space-y-5" data-testid="pipeline-analysis">
      {/* Health Score */}
      <div className="flex items-center justify-between">
        <HealthBadge score={data.health_score} />
        <span className="text-[10px] text-slate-600">{data.total_leads} leads / {data.total_stages} étapes</span>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: 'Moy/étape', value: data.avg_leads_per_stage, icon: Target, color: 'text-blue-400' },
          { label: 'Alertes', value: data.insights_count, icon: AlertCircle, color: data.insights_count > 0 ? 'text-amber-400' : 'text-emerald-400' },
          { label: 'Étapes vides', value: data.empty_stages?.length || 0, icon: AlertTriangle, color: data.empty_stages?.length > 3 ? 'text-red-400' : 'text-slate-400' },
        ].map((kpi, i) => (
          <div key={i} className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-2.5 text-center">
            <kpi.icon className={`w-3.5 h-3.5 mx-auto mb-1 ${kpi.color}`} />
            <div className="text-sm font-bold text-white">{kpi.value}</div>
            <div className="text-[10px] text-slate-600">{kpi.label}</div>
          </div>
        ))}
      </div>

      {/* Distribution by category */}
      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Distribution par catégorie</h4>
        <BarChart items={data.categories || []} maxValue={Math.max(...(data.categories?.map(c => c.count) || [1]))} />
      </div>

      {/* Top stages */}
      <div>
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Top étapes</h4>
        <BarChart items={data.top_stages || []} maxValue={maxLeads} />
      </div>

      {/* Bottlenecks */}
      {data.bottlenecks?.length > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-amber-400 mb-2">Goulots d'étranglement</h4>
          <div className="space-y-1.5">
            {data.bottlenecks.map((b, i) => (
              <div key={i} className={`flex items-center gap-2 px-2.5 py-2 rounded-lg border ${
                b.severity === 'high' ? 'bg-red-500/5 border-red-500/20' : 'bg-amber-500/5 border-amber-500/20'
              }`} data-testid={`bottleneck-${b.id}`}>
                <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 ${b.severity === 'high' ? 'text-red-400' : 'text-amber-400'}`} />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-slate-300 truncate block">{b.name}</span>
                  <span className="text-[10px] text-slate-600">{b.count} leads ({b.ratio}x moy.)</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Dead-ends */}
      {data.dead_ends?.length > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-red-400 mb-2">Étapes sans sortie</h4>
          <div className="space-y-1">
            {data.dead_ends.map((d, i) => (
              <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-red-500/5 border border-red-500/15">
                <AlertCircle className="w-3 h-3 text-red-400 flex-shrink-0" />
                <span className="text-xs text-slate-400 truncate">{d.name}</span>
                {d.count > 0 && <span className="text-[10px] text-red-400/60 ml-auto">{d.count} bloqués</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unmapped stages (in clients but not in config) */}
      {data.unmapped_stages?.length > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-orange-400 mb-2">Étapes non-configurées ({data.unmapped_stages.length})</h4>
          <p className="text-[10px] text-slate-600 mb-2">Étapes présentes dans les données clients mais absentes de la config pipeline</p>
          <div className="space-y-1">
            {data.unmapped_stages.slice(0, 8).map((u, i) => (
              <div key={i} className="flex items-center justify-between px-2.5 py-1.5 rounded-lg bg-orange-500/5 border border-orange-500/15">
                <span className="text-xs text-slate-400 truncate">{u.name}</span>
                <span className="text-[10px] text-orange-400/70">{u.count} leads</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════
// STAGE EDITOR PANEL
// ════════════════════════════════════════
const COLORS = ['#3B82F6','#8B5CF6','#10B981','#F59E0B','#EF4444','#06B6D4','#EC4899','#6B7280'];
const CATEGORIES = [
  { id: 'avant_r1', label: 'Avant R1', color: 'text-blue-400' },
  { id: 'apres_r1', label: 'Après R1', color: 'text-violet-400' },
  { id: 'final', label: 'Final', color: 'text-emerald-400' },
];

const StageEditor = ({ refreshKey, onRefresh }) => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [adding, setAdding] = useState(false);
  const [newStage, setNewStage] = useState({ nom: '', categorie: 'avant_r1', color: '#3B82F6' });
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await robustFetch(`/api/pipeline-workflow/stages`);
        setStages(data.stages || data || []);
      } catch {}
      setLoading(false);
    };
    load();
  }, [refreshKey]);

  const startEdit = (s) => {
    setEditing(s.id || s.stage_id);
    setEditData({ nom: s.nom || s.stage_name, color: s.color || s.stage_color || '#3B82F6', categorie: s.categorie || 'avant_r1' });
  };

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const data = await robustFetch(`/api/workflow-ai/execute-action`, {
        method: 'POST',
        body: JSON.stringify({ action_type: 'update_stage', payload: { stage_id: editing, updates: editData } }),
      });
      if (data.success) {
        setStages(prev => prev.map(s => (s.id || s.stage_id) === editing ? { ...s, ...editData, stage_name: editData.nom, stage_color: editData.color } : s));
        setEditing(null);
        onRefresh();
      }
    } catch {}
    setSaving(false);
  };

  const addStage = async () => {
    if (!newStage.nom.trim()) return;
    setSaving(true);
    const id = newStage.nom.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    try {
      const data = await robustFetch(`/api/workflow-ai/execute-action`, {
        method: 'POST',
        body: JSON.stringify({
          action_type: 'add_stages',
          payload: { stages: [{ id, nom: newStage.nom, color: newStage.color, categorie: newStage.categorie, ordre: stages.length + 1, next: [] }] },
        }),
      });
      if (data.success) {
        setAdding(false);
        setNewStage({ nom: '', categorie: 'avant_r1', color: '#3B82F6' });
        onRefresh();
      }
    } catch {}
    setSaving(false);
  };

  const deleteStage = async (sid) => {
    setSaving(true);
    try {
      const data = await robustFetch(`/api/workflow-ai/execute-action`, {
        method: 'POST',
        body: JSON.stringify({ action_type: 'delete_stage', payload: { stage_id: sid } }),
      });
      if (data.success) {
        setStages(prev => prev.filter(s => (s.id || s.stage_id) !== sid));
        setConfirmDel(null);
        onRefresh();
      }
    } catch {}
    setSaving(false);
  };

  const handleDragStart = (e, idx) => { e.dataTransfer.setData('text/plain', idx.toString()); };
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOver(idx); };
  const handleDrop = async (e, dropIdx) => {
    e.preventDefault();
    const dragIdx = parseInt(e.dataTransfer.getData('text/plain'));
    if (isNaN(dragIdx) || dragIdx === dropIdx) { setDragOver(null); return; }

    const newStages = [...stages];
    const [moved] = newStages.splice(dragIdx, 1);
    newStages.splice(dropIdx, 0, moved);
    setStages(newStages);
    setDragOver(null);

    const order = newStages.map((s, i) => ({ id: s.id || s.stage_id, ordre: i + 1 }));
    await robustFetch(`/api/workflow-ai/execute-action`, {
      method: 'POST',
      body: JSON.stringify({ action_type: 'reorder_stages', payload: { order } }),
    });
    onRefresh();
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;

  const groups = {};
  stages.forEach((s, i) => {
    const cat = s.categorie || 'autre';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push({ ...s, _idx: i });
  });

  return (
    <div className="space-y-4" data-testid="stage-editor">
      {/* Add button */}
      {!adding ? (
        <button onClick={() => setAdding(true)} data-testid="add-stage-btn"
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-violet-600/10 border border-violet-500/20 text-violet-400 text-xs font-semibold hover:bg-violet-600/20 transition-all">
          <Plus className="w-3.5 h-3.5" /> Ajouter une étape
        </button>
      ) : (
        <div className="bg-slate-800/60 border border-violet-500/30 rounded-xl p-3 space-y-2" data-testid="add-stage-form">
          <input value={newStage.nom} onChange={e => setNewStage(p => ({ ...p, nom: e.target.value }))}
            placeholder="Nom de l'étape" data-testid="new-stage-name"
            className="w-full bg-slate-900/60 border border-slate-700/40 rounded-lg px-3 py-2 text-xs text-white placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-violet-500/50" />
          <div className="flex gap-2">
            <select value={newStage.categorie} onChange={e => setNewStage(p => ({ ...p, categorie: e.target.value }))}
              data-testid="new-stage-category"
              className="flex-1 bg-slate-900/60 border border-slate-700/40 rounded-lg px-2 py-1.5 text-xs text-white focus:outline-none">
              {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <div className="flex gap-1">
              {COLORS.slice(0, 5).map(c => (
                <button key={c} onClick={() => setNewStage(p => ({ ...p, color: c }))}
                  className={`w-6 h-6 rounded-md border-2 transition-all ${newStage.color === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={addStage} disabled={!newStage.nom.trim() || saving} data-testid="confirm-add-stage"
              className="flex-1 bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 text-white text-xs font-semibold py-1.5 rounded-lg transition-all">
              {saving ? <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> : <Check className="w-3 h-3 inline mr-1" />}Ajouter
            </button>
            <button onClick={() => setAdding(false)} className="px-3 py-1.5 bg-slate-700 text-slate-400 text-xs rounded-lg hover:bg-slate-600 transition-all">Annuler</button>
          </div>
        </div>
      )}

      {/* Stages grouped by category */}
      {CATEGORIES.map(cat => {
        const catStages = groups[cat.id] || [];
        if (catStages.length === 0) return null;
        return (
          <div key={cat.id}>
            <div className={`text-[11px] font-semibold uppercase tracking-wider ${cat.color} mb-2`}>
              {cat.label} ({catStages.length})
            </div>
            <div className="space-y-1">
              {catStages.map((s) => {
                const sid = s.id || s.stage_id;
                const isEditing = editing === sid;
                return (
                  <div key={sid} draggable={!isEditing} onDragStart={e => handleDragStart(e, s._idx)}
                    onDragOver={e => handleDragOver(e, s._idx)} onDrop={e => handleDrop(e, s._idx)}
                    onDragLeave={() => setDragOver(null)}
                    data-testid={`stage-editor-item-${sid}`}
                    className={`group rounded-lg border transition-all ${
                      dragOver === s._idx ? 'border-violet-500/50 bg-violet-500/10' :
                      isEditing ? 'border-violet-500/40 bg-slate-800/80' :
                      'border-slate-700/30 bg-slate-800/40 hover:border-slate-600/50'
                    }`}>
                    {isEditing ? (
                      <div className="p-2.5 space-y-2">
                        <input value={editData.nom} onChange={e => setEditData(p => ({ ...p, nom: e.target.value }))}
                          className="w-full bg-slate-900/60 border border-slate-700/40 rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-1 focus:ring-violet-500/50" />
                        <div className="flex gap-2 items-center">
                          <select value={editData.categorie} onChange={e => setEditData(p => ({ ...p, categorie: e.target.value }))}
                            className="flex-1 bg-slate-900/60 border border-slate-700/40 rounded-lg px-2 py-1 text-[11px] text-white focus:outline-none">
                            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                          </select>
                          <div className="flex gap-1">
                            {COLORS.map(c => (
                              <button key={c} onClick={() => setEditData(p => ({ ...p, color: c }))}
                                className={`w-5 h-5 rounded border-2 transition-all ${editData.color === c ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'}`}
                                style={{ backgroundColor: c }} />
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} disabled={saving}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-[11px] font-semibold py-1 rounded-lg transition-all">
                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                          </button>
                          <button onClick={() => setEditing(null)}
                            className="px-2 py-1 bg-slate-700 text-slate-400 text-[11px] rounded-lg hover:bg-slate-600">Annuler</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-2.5 py-2 cursor-grab">
                        <ArrowUpDown className="w-3 h-3 text-slate-700 group-hover:text-slate-500 flex-shrink-0" />
                        <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || s.stage_color || '#6B7280' }} />
                        <span className="text-xs text-slate-300 truncate flex-1">{s.nom || s.stage_name}</span>
                        <button onClick={() => startEdit(s)} data-testid={`edit-stage-${sid}`}
                          className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-700 text-slate-500 hover:text-white transition-all">
                          <RefreshCw className="w-3 h-3" />
                        </button>
                        {confirmDel === sid ? (
                          <button onClick={() => deleteStage(sid)} data-testid={`confirm-del-${sid}`}
                            className="px-2 py-0.5 bg-red-600 text-white text-[10px] rounded font-semibold animate-pulse">Supprimer ?</button>
                        ) : (
                          <button onClick={() => { setConfirmDel(sid); setTimeout(() => setConfirmDel(null), 3000); }}
                            data-testid={`delete-stage-${sid}`}
                            className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-500/20 text-slate-600 hover:text-red-400 transition-all">
                            <Trash2 className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Ungrouped stages */}
      {groups['autre']?.length > 0 && (
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Autre ({groups['autre'].length})</div>
          <div className="space-y-1">
            {groups['autre'].map(s => {
              const sid = s.id || s.stage_id;
              return (
                <div key={sid} className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-800/40 border border-slate-700/30">
                  <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color || '#6B7280' }} />
                  <span className="text-xs text-slate-400 truncate">{s.nom || s.stage_name}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════
// MAIN CONFIGURATOR PAGE
// ════════════════════════════════════════
const WorkflowAIConfigurator = () => {
  const [provider, setProvider] = useState(() => localStorage.getItem('wf_ai_provider') || 'openai');
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(() => genId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executedActions, setExecutedActions] = useState(new Set());
  const [showSessions, setShowSessions] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rightTab, setRightTab] = useState('analysis');
  const messagesEndRef = useRef(null);
  const sendingRef = useRef(false);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollToBottom, [messages]);
  useEffect(() => { localStorage.setItem('wf_ai_provider', provider); }, [provider]);
  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      setSessions((await robustFetch(`/api/workflow-ai/sessions`)).sessions || []);
    } catch {}
  };

  const loadSession = async (sid) => {
    try {
      const data = await robustFetch(`/api/workflow-ai/sessions/${sid}`);
      setMessages(data.messages || []);
      setSessionId(sid);
      setShowSessions(false);
      setExecutedActions(new Set());
    } catch {}
  };

  const newSession = () => { setSessionId(genId()); setMessages([]); setShowSessions(false); setExecutedActions(new Set()); };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    await robustFetch(`/api/workflow-ai/sessions/${sid}`, { method: 'DELETE' });
    setSessions(prev => prev.filter(s => s.session_id !== sid));
    if (sessionId === sid) newSession();
  };

  const sendMessage = useCallback(async () => {
    if (!input.trim() || sendingRef.current) return;
    sendingRef.current = true;
    const msg = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const tenantId = localStorage.getItem('active_tenant_id');
      const res = await fetch(`${API_BASE}/api/workflow-ai/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(tenantId ? { 'X-Tenant-Id': tenantId } : {}),
        },
        body: JSON.stringify({ session_id: sessionId, message: msg, provider }),
      });
      const text = await res.text();
      if (!res.ok) {
        let errMsg = `HTTP ${res.status}`;
        try { errMsg = JSON.parse(text).detail || errMsg; } catch {}
        throw new Error(errMsg);
      }
      const data = JSON.parse(text);
      setMessages(prev => [...prev, { role: 'assistant', content: data.response, actions: data.actions || [], timestamp: new Date().toISOString() }]);
      loadSessions();
    } catch (err) {
      console.error('Chat send error:', err);
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur: ${err.message}`, timestamp: new Date().toISOString() }]);
    } finally {
      sendingRef.current = false;
      setLoading(false);
    }
  }, [input, sessionId, provider]);

  const executeAction = async (action, idx) => {
    setExecuting(true);
    try {
      const data = await robustFetch(`/api/workflow-ai/execute-action`, {
        method: 'POST',
        body: JSON.stringify({ action_type: action.type, payload: action }),
      });
      if (data.detail) throw new Error(data.detail);
      setExecutedActions(prev => new Set([...prev, idx]));
      setRefreshKey(k => k + 1);
      setMessages(prev => [...prev, { role: 'assistant', content: `Action appliquée : ${data.message}`, timestamp: new Date().toISOString() }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Erreur: ${err.message}`, timestamp: new Date().toISOString() }]);
    } finally { setExecuting(false); }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  let actionIndex = 0;
  const suggestions = [
    "Analyse le pipeline et identifie les goulots d'étranglement",
    "Ajoute une étape 'Qualification' entre le premier appel et le R1",
    "Configure les triggers de sortie pour les étapes sans transitions",
    "Propose un réordonnancement des étapes pour optimiser le flux",
  ];

  return (
    <div className="flex gap-4 h-[calc(100vh-220px)]" data-testid="ai-configurator-page">
      {/* LEFT: Chat (60%) */}
      <div className="flex-[3] flex flex-col bg-slate-900/50 border border-slate-700/30 rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-slate-700/40 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-600 to-blue-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-white">Yaël Cohen — Configurateur Workflow</h2>
              <p className="text-[11px] text-slate-500">Directrice IT / Telecom / IA</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center bg-slate-800/70 rounded-lg p-0.5 gap-0.5">
              {[{ id: 'openai', label: 'GPT' }, { id: 'anthropic', label: 'Claude' }].map(p => (
                <button key={p.id} onClick={() => setProvider(p.id)} data-testid={`provider-${p.id}`}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${provider === p.id ? 'bg-violet-600 text-white shadow-lg' : 'text-slate-500 hover:text-white'}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <button onClick={() => setShowSessions(!showSessions)} data-testid="toggle-sessions"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all">
              <MessageSquare className="w-4 h-4" />
            </button>
            <button onClick={newSession} data-testid="new-session"
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Sessions list */}
        {showSessions && (
          <div className="border-b border-slate-700/40 max-h-40 overflow-y-auto bg-slate-900/80">
            {sessions.length === 0 ? (
              <p className="text-xs text-slate-600 p-3 text-center">Aucune session</p>
            ) : sessions.map(s => (
              <div key={s.session_id} onClick={() => loadSession(s.session_id)}
                className={`flex items-center justify-between px-4 py-2.5 cursor-pointer hover:bg-slate-800/60 transition-all ${sessionId === s.session_id ? 'bg-slate-800/80 border-l-2 border-violet-500' : ''}`}>
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-slate-300 block truncate">{s.title || 'Sans titre'}</span>
                  <span className="text-[10px] text-slate-600">{s.message_count || 0} messages</span>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="text-slate-700 hover:text-red-400 ml-2 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-600/20 to-blue-600/20 flex items-center justify-center mb-4 border border-violet-500/20">
                <Sparkles className="w-8 h-8 text-violet-400" />
              </div>
              <h3 className="text-base font-bold text-white mb-2">Configurateur Workflow IA</h3>
              <p className="text-sm text-slate-500 max-w-sm mb-6">
                Discutez avec Yaël pour ajouter des étapes, configurer des transitions et améliorer votre pipeline.
              </p>
              <div className="space-y-2 w-full max-w-md">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => setInput(s)} data-testid={`suggestion-${i}`}
                    className="w-full text-left px-4 py-2.5 rounded-xl bg-slate-800/40 border border-slate-700/30 text-xs text-slate-400 hover:text-white hover:bg-slate-800/70 hover:border-violet-500/30 transition-all">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((msg, i) => (
            <div key={i} className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'assistant' && (
                <div className="w-7 h-7 rounded-lg bg-violet-600/20 border border-violet-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Bot className="w-4 h-4 text-violet-400" />
                </div>
              )}
              <div className="max-w-[80%] space-y-2">
                <div className={`rounded-2xl px-4 py-3 ${msg.role === 'user' ? 'bg-violet-600/20 border border-violet-500/20 text-slate-200' : 'bg-slate-800/50 border border-slate-700/30 text-slate-300'}`}>
                  <p className="text-sm whitespace-pre-wrap leading-relaxed">{cleanText(msg.content)}</p>
                </div>
                {msg.actions?.map((action, j) => {
                  const currentIdx = actionIndex++;
                  return <ActionCard key={j} action={action} executing={executing} executed={executedActions.has(currentIdx)} onExecute={(a) => executeAction(a, currentIdx)} />;
                })}
              </div>
              {msg.role === 'user' && (
                <div className="w-7 h-7 rounded-lg bg-blue-600/20 border border-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <User className="w-4 h-4 text-blue-400" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="w-7 h-7 rounded-lg bg-violet-600/20 border border-violet-500/20 flex items-center justify-center">
                <Bot className="w-4 h-4 text-violet-400" />
              </div>
              <div className="bg-slate-800/50 border border-slate-700/30 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin text-violet-400" /><span className="text-xs text-slate-500">Yaël réfléchit...</span></div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-700/40 bg-slate-900/30">
          <div className="flex gap-3">
            <textarea value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
              placeholder="Décrivez les étapes à ajouter ou demandez une analyse..."
              data-testid="ai-chat-input" rows={2}
              className="flex-1 bg-slate-800/60 border border-slate-700/40 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-600 resize-none focus:outline-none focus:ring-1 focus:ring-violet-500/50 focus:border-violet-500/30" />
            <button onClick={sendMessage} disabled={!input.trim() || loading} data-testid="ai-chat-send"
              className="self-end bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:text-slate-600 text-white p-3 rounded-xl transition-all">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* RIGHT: Analysis + Pipeline + History (40%) */}
      <div className="flex-[2] flex flex-col bg-slate-900/50 border border-slate-700/30 rounded-2xl overflow-hidden">
        <div className="p-3 border-b border-slate-700/40 flex items-center gap-1">
          {[
            { id: 'analysis', label: 'Analyse', icon: BarChart3 },
            { id: 'editor', label: 'Éditeur', icon: Settings2 },
            { id: 'pipeline', label: 'Pipeline', icon: Sparkles },
            { id: 'history', label: 'Historique', icon: History },
          ].map(tab => (
            <button key={tab.id} onClick={() => setRightTab(tab.id)} data-testid={`right-tab-${tab.id}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                rightTab === tab.id ? 'bg-violet-600/20 text-violet-300 border border-violet-500/30' : 'text-slate-500 hover:text-white hover:bg-slate-800/50'
              }`}>
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
          <button onClick={() => setRefreshKey(k => k + 1)} data-testid="refresh-preview"
            className="ml-auto p-1.5 text-slate-500 hover:text-white rounded-lg hover:bg-slate-700/50 transition-all">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {rightTab === 'analysis' && <PipelineAnalysis refreshKey={refreshKey} />}
          {rightTab === 'editor' && <StageEditor refreshKey={refreshKey} onRefresh={() => setRefreshKey(k => k + 1)} />}
          {rightTab === 'pipeline' && <PipelinePreview refreshKey={refreshKey} />}
          {rightTab === 'history' && <ChangesHistory refreshKey={refreshKey} />}
        </div>
      </div>
    </div>
  );
};

export default WorkflowAIConfigurator;
